import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';
import { formatDate } from '../utils/dateFormat';

const toDateInputValue = (date) => {
  if (!date || Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const calculateRenewalDate = (installationDate, validityMonths) => {
  const date = installationDate ? new Date(installationDate) : new Date();
  if (Number.isNaN(date.getTime())) return '';
  date.setMonth(date.getMonth() + Number(validityMonths || 0));
  return toDateInputValue(date);
};

export default function EmployeePanel() {
  const [stats, setStats] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [address, setAddress] = useState('');
  const [vehicle, setVehicle] = useState('');
  const [device, setDevice] = useState('GPS');
  const [imei, setImei] = useState('');
  const [installDate, setInstallDate] = useState('');
  const [validity, setValidity] = useState(12);
  const [renewalDate, setRenewalDate] = useState(calculateRenewalDate('', 12));
  const [payStatus, setPayStatus] = useState('pending');
  const [dealerId, setDealerId] = useState('');

  const getHeaders = useCallback(() => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }), []);

  const fetchAll = useCallback(async () => {
    try {
      const [statsRes, custRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/api/users/employee/panel`, { headers: getHeaders() }),
        axios.get(`${API_URL}/api/customers`, { headers: getHeaders() }),
        axios.get(`${API_URL}/api/users`, { headers: getHeaders() }),
      ]);
      setStats(statsRes.data);
      setCustomers(custRes.data);
      setDealers(usersRes.data.filter(u => u.role === 'dealer'));
    } catch (err) { console.log(err); }
  }, [getHeaders]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const resetForm = () => {
    setName(''); setMobile(''); setAddress(''); setVehicle('');
    setImei(''); setInstallDate(''); setValidity(12); setRenewalDate(calculateRenewalDate('', 12));
    setPayStatus('pending'); setDealerId('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      await axios.post(`${API_URL}/api/customers`, {
        name, mobile, address, vehicle_no: vehicle, device_type: device,
        imei, installation_date: installDate, validity_months: validity, renewal_date: renewalDate,
        payment_status: payStatus, dealer_id: dealerId || null
      }, { headers: getHeaders() });
      alert('Customer added successfully!');
      setShowForm(false); resetForm(); fetchAll();
    } catch (err) {
      alert(err.response?.data?.message || 'An error occurred');
    } finally { setLoading(false); }
  };

  const inp = { width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' };
  const lbl = { display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 'bold', color: '#374151' };
  const updateInstallDate = (value) => {
    setInstallDate(value);
    setRenewalDate(calculateRenewalDate(value, validity));
  };
  const updateValidity = (value) => {
    setValidity(value);
    setRenewalDate(calculateRenewalDate(installDate, value));
  };

  return (
    <div className="fixed-panel-page">
      <div className="fixed-panel-header">
      <h2 style={{ color: '#1e3a5f', marginBottom: '20px' }}>My Employee Panel</h2>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
        {[
          { label: 'Total Customers Added', value: stats.total_customers_added || 0, color: '#3b82f6' },
          { label: 'Added Today', value: stats.today_added || 0, color: '#10b981' },
          { label: 'Total Sales', value: stats.total_sales || 0, color: '#8b5cf6' },
        ].map(card => (
          <div key={card.label} style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', borderTop: `4px solid ${card.color}` }}>
            <p style={{ margin: '0 0 8px', fontSize: '13px', color: '#6b7280' }}>{card.label}</p>
            <p style={{ margin: 0, fontSize: '28px', fontWeight: 'bold', color: card.color }}>{card.value}</p>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3 style={{ color: '#1e3a5f', margin: 0 }}>Customer List</h3>
        <button onClick={() => setShowForm(!showForm)} style={{ background: '#1e3a5f', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}>
          {showForm ? 'Cancel' : '+ Add Customer'}
        </button>
      </div>
      </div>

      <div className="fixed-panel-body">
      {showForm && (
        <div style={{ background: 'white', borderRadius: '12px', padding: '24px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <h3 style={{ color: '#1e3a5f', marginTop: 0 }}>Add New Customer</h3>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div><label style={lbl}>Full Name *</label><input style={inp} value={name} onChange={e => setName(e.target.value)} placeholder="Customer name" required /></div>
              <div><label style={lbl}>Mobile *</label><input style={inp} value={mobile} onChange={e => setMobile(e.target.value)} placeholder="Mobile number" required /></div>
              <div><label style={lbl}>Vehicle Number</label><input style={inp} value={vehicle} onChange={e => setVehicle(e.target.value)} placeholder="DL01AB1234" /></div>
              <div><label style={lbl}>IMEI</label><input style={inp} value={imei} onChange={e => setImei(e.target.value)} placeholder="15 digit IMEI" /></div>
              <div><label style={lbl}>Installation Date</label><input type="date" style={inp} value={installDate} onChange={e => updateInstallDate(e.target.value)} /></div>
              <div><label style={lbl}>Validity (Months)</label><input type="number" style={inp} value={validity} onChange={e => updateValidity(e.target.value)} /></div>
              <div><label style={lbl}>Renewal / Expiry Date</label><input type="date" style={inp} value={renewalDate} onChange={e => setRenewalDate(e.target.value)} /></div>
              <div>
                <label style={lbl}>Device Type</label>
                <select style={inp} value={device} onChange={e => setDevice(e.target.value)}>
                  <option>GPS</option><option>AIS140</option><option>Camera</option><option>SIM</option>
                </select>
              </div>
              <div>
                <label style={lbl}>Payment Status</label>
                <select style={inp} value={payStatus} onChange={e => setPayStatus(e.target.value)}>
                  <option value="pending">Pending</option><option value="paid">Paid</option><option value="partial">Partial</option>
                </select>
              </div>
              <div>
                <label style={lbl}>Dealer (Optional)</label>
                <select style={inp} value={dealerId} onChange={e => setDealerId(e.target.value)}>
                  <option value="">Direct (No Dealer)</option>
                  {dealers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
              <div><label style={lbl}>Address</label><input style={inp} value={address} onChange={e => setAddress(e.target.value)} placeholder="Full address" /></div>
            </div>
            <button type="submit" disabled={loading} style={{ marginTop: '16px', background: '#10b981', color: 'white', border: 'none', padding: '12px 24px', borderRadius: '8px', fontSize: '15px', cursor: 'pointer', fontWeight: 'bold' }}>
              {loading ? 'Saving...' : 'Save Customer'}
            </button>
          </form>
        </div>
      )}

      <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#1e3a5f', color: 'white' }}>
              {['#', 'Name', 'Vehicle No', 'Device', 'IMEI', 'Installation Date', 'Renewal Date', 'Payment'].map(h => (
                <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {customers.length === 0 ? (
              <tr><td colSpan="8" style={{ padding: '24px', textAlign: 'center', color: '#666' }}>No customers found</td></tr>
            ) : customers.map((c, i) => (
              <tr key={c.id} style={{ borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? 'white' : '#f9fafb' }}>
                <td style={{ padding: '12px 16px', fontSize: '13px' }}>{i + 1}</td>
                <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: 'bold' }}>{c.name}</td>
                <td style={{ padding: '12px 16px', fontSize: '13px' }}>{c.vehicle_no || '-'}</td>
                <td style={{ padding: '12px 16px', fontSize: '13px' }}>{c.device_type || '-'}</td>
                <td style={{ padding: '12px 16px', fontSize: '13px' }}>{c.imei || '-'}</td>
                <td style={{ padding: '12px 16px', fontSize: '13px' }}>{c.installation_date ? formatDate(c.installation_date) : '-'}</td>
                <td style={{ padding: '12px 16px', fontSize: '13px' }}>{c.renewal_date ? formatDate(c.renewal_date) : '-'}</td>
                <td style={{ padding: '12px 16px' }}>
                  <span style={{ padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold', background: c.payment_status === 'paid' ? '#d1fae5' : c.payment_status === 'pending' ? '#fee2e2' : '#fef3c7', color: c.payment_status === 'paid' ? '#065f46' : c.payment_status === 'pending' ? '#dc2626' : '#92400e' }}>
                    {c.payment_status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </div>
    </div>
  );
}
